import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PROGRAMS } from "@/data/content";

const initial = {
  name: "", phone: "", email: "", program_interest: "",
  preferred_contact: "phone", relationship_to_client: "", message: "", consent: true,
};

const LeadForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and a phone number so we can reach you.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, email: form.email.trim() || null };
      await api.post("/leads", payload);
      setDone(true);
      toast.success("Thank you for reaching out. Our team will call you shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-form-success" className="rounded-3xl bg-white border border-line shadow-[0_20px_40px_rgb(0,0,0,0.08)] p-10 text-center">
        <div className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-sage/20 text-forest">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="mt-6 font-serif text-3xl text-forest">We’ve received your message</h3>
        <p className="mt-3 text-ink2 leading-relaxed">
          You’ve taken the bravest step. A member of our admissions team will reach out very soon —
          usually within the hour. You are not alone.
        </p>
        <Button data-testid="lead-form-reset" onClick={() => setDone(false)} variant="outline" className="mt-8 rounded-full border-forest text-forest">
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="lead-form"
      className="rounded-3xl bg-white border border-line shadow-[0_20px_40px_rgb(0,0,0,0.08)] p-6 md:p-9"
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="lf-name" className="text-ink font-medium">Your name *</Label>
          <Input id="lf-name" data-testid="lead-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="First and last name" className="h-12 rounded-xl border-line bg-sand/40" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lf-phone" className="text-ink font-medium">Phone number *</Label>
          <Input id="lf-phone" data-testid="lead-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(647) 000-0000" className="h-12 rounded-xl border-line bg-sand/40" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lf-email" className="text-ink font-medium">Email</Label>
          <Input id="lf-email" data-testid="lead-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="h-12 rounded-xl border-line bg-sand/40" />
        </div>
        <div className="space-y-2">
          <Label className="text-ink font-medium">Program of interest</Label>
          <Select value={form.program_interest} onValueChange={(v) => update("program_interest", v)}>
            <SelectTrigger data-testid="lead-program" className="h-12 rounded-xl border-line bg-sand/40">
              <SelectValue placeholder="Select a program" />
            </SelectTrigger>
            <SelectContent>
              {PROGRAMS.map((p) => (
                <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
              ))}
              <SelectItem value="Not sure yet">I’m not sure yet</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-ink font-medium">This enquiry is for</Label>
          <Select value={form.relationship_to_client} onValueChange={(v) => update("relationship_to_client", v)}>
            <SelectTrigger data-testid="lead-relationship" className="h-12 rounded-xl border-line bg-sand/40">
              <SelectValue placeholder="Myself / a loved one" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Myself">Myself</SelectItem>
              <SelectItem value="A family member">A family member</SelectItem>
              <SelectItem value="A friend">A friend</SelectItem>
              <SelectItem value="A client / patient">A client or patient</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-ink font-medium">Best way to reach you</Label>
          <Select value={form.preferred_contact} onValueChange={(v) => update("preferred_contact", v)}>
            <SelectTrigger data-testid="lead-contact-method" className="h-12 rounded-xl border-line bg-sand/40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="phone">Phone call</SelectItem>
              <SelectItem value="text">Text message</SelectItem>
              <SelectItem value="email">Email</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <Label htmlFor="lf-msg" className="text-ink font-medium">How can we help? (optional)</Label>
        <Textarea id="lf-msg" data-testid="lead-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us a little about your situation. Everything you share is confidential." rows={4} className="rounded-xl border-line bg-sand/40 resize-none" />
      </div>

      <label className="mt-5 flex items-start gap-3 cursor-pointer">
        <Checkbox data-testid="lead-consent" checked={form.consent} onCheckedChange={(v) => update("consent", !!v)} className="mt-1 border-forest data-[state=checked]:bg-forest" />
        <span className="text-sm text-ink2 leading-relaxed">
          I consent to being contacted by New Beginnings Recovery about treatment options. My information will be kept strictly confidential.
        </span>
      </label>

      <Button type="submit" data-testid="lead-submit" disabled={loading} className="mt-6 w-full h-14 rounded-full bg-terracotta hover:bg-[#a8664b] text-white text-base font-semibold transition-colors active:scale-[0.99]">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Request a confidential call back"}
      </Button>

      <p className="mt-4 flex items-center justify-center gap-2 text-xs text-ink2">
        <ShieldCheck className="w-4 h-4 text-sage" /> 100% confidential · No obligation · Immediate admittance available
      </p>
    </form>
  );
};

export default LeadForm;
